import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, ArrowUpRight } from "lucide-react";
import { waLink, WHATSAPP_DISPLAY, CONTACT_EMAIL, OFFICE_ADDRESS, INSTAGRAM_URL, INSTAGRAM_HANDLE, SISTER_BRAND } from "@/lib/constants";
import { STATIONERY_NAV } from "@/lib/stationeryNav";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#F8EDE7] text-[#0E0709] mt-16 md:mt-24 overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#B8915E] to-transparent shimmer-line" />

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 mb-12">
          <div className="md:col-span-7">
            <p className="overline text-[#B8915E] mb-7">Begin your story</p>
            <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-normal leading-[0.92] tracking-tight">
              Luxury,
              <br />
              <span className="italic-serif text-[#D4B896]">accessibly crafted.</span>
            </h2>
            <p className="mt-8 text-base font-normal text-[#0E0709]/65 max-w-md leading-relaxed">
              Talk to a designer. Receive samples in hand. Watch your vision come to life on paper.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="footer-book-cta"
                className="btn-luxe bg-[#B8915E] text-[#F8EDE7] px-8 py-4 text-[12px] tracking-[0.2em] uppercase inline-flex items-center gap-3"
              >
                Book a Meeting <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href={waLink("Hi! I want an instant quote.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-cta"
                className="btn-outline-luxe border border-[#0E0709]/35 text-[#0E0709] px-8 py-4 text-[12px] tracking-[0.2em] uppercase"
              >
                WhatsApp Now
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-[#B8915E] mb-6">Browse</p>
            <ul className="space-y-3 text-sm font-normal">
              {[
                { to: "/", l: "Home" },
                { to: "/about", l: "Atelier" },
                { to: "/team", l: "Meet the Team" },
                { to: "/services", l: "Collection" },
                { to: "/book", l: "Book Meeting" },
                { to: "/career", l: "Careers" },
                { to: "/journal", l: "Journal" },
                { to: "/contact", l: "Contact" },
              ].map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="hover:text-[#B8915E] transition-colors">{i.l}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline text-[#B8915E] mb-6">Reach Out</p>
            <ul className="space-y-5 text-sm font-normal">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[#B8915E]" />
                <a href={waLink()} className="hover:text-[#B8915E] transition-colors" data-testid="footer-phone">
                  {WHATSAPP_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#B8915E]" />
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email" className="hover:text-[#B8915E] transition-colors">
                  {CONTACT_EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#B8915E]" />
                <a
                  href={OFFICE_ADDRESS.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-map-link"
                  className="text-[#0E0709]/65 leading-relaxed hover:text-[#B8915E] transition-colors"
                >
                  {OFFICE_ADDRESS.line1}<br/>
                  {OFFICE_ADDRESS.line2}
                  <span className="block text-[10px] tracking-[0.22em] uppercase text-[#B8915E] mt-1">Get directions →</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Instagram className="w-4 h-4 mt-0.5 text-[#B8915E]" />
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="hover:text-[#B8915E] transition-colors">@{INSTAGRAM_HANDLE}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-10 border-t border-[#0E0709]/12 mb-10" data-testid="footer-stationery-section">
          <p className="overline text-[#B8915E] mb-5">Wedding Stationery</p>
          <ul className="flex flex-wrap gap-x-7 gap-y-3 text-sm font-normal">
            {STATIONERY_NAV.map((s) => (
              <li key={s.slug}>
                <Link to={`/${s.slug}`} data-testid={`footer-stationery-${s.slug}`} className="hover:text-[#B8915E] transition-colors">
                  {s.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/portfolio" className="text-[#B8915E] hover:text-[#0E0709] transition-colors">
                View Portfolio →
              </Link>
            </li>
          </ul>
        </div>

        <div className="pt-10 border-t border-[#0E0709]/12 mb-8">
          <a
            href={SISTER_BRAND.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-sister-brand"
            className="group inline-flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-7 px-6 py-5 border border-[#0E0709]/15 hover:border-[#B8915E] transition-colors duration-500 bg-[#F8EDE7] hover:bg-[#0E0709]/5 max-w-2xl"
          >
            <div className="flex items-center gap-3">
              <span className="overline text-[#B8915E] whitespace-nowrap">Sister Brand</span>
              <span className="hidden sm:block w-px h-6 bg-[#0E0709]/15" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-2xl text-[#0E0709] italic-serif leading-tight">
                {SISTER_BRAND.name}
              </p>
              <p className="text-[11px] text-[#0E0709]/55 tracking-wide mt-1">
                {SISTER_BRAND.tagline} · @{SISTER_BRAND.instagramHandle}
              </p>
            </div>
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#0E0709] group-hover:text-[#B8915E] transition-colors whitespace-nowrap">
              {SISTER_BRAND.domain}
              <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </a>
        </div>

        <div className="pt-8 border-t border-[#0E0709]/12 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <p className="text-[11px] text-[#0E0709]/45 tracking-[0.18em] uppercase">
            © {new Date().getFullYear()} Stationery Wala × Invitation Wala — All rights reserved
          </p>
          <div className="flex flex-wrap gap-5 text-[11px] text-[#0E0709]/55 tracking-[0.15em] uppercase">
            <span>DTDC</span>
            <span className="text-[#B8915E]">·</span>
            <span>Blue Dart</span>
            <span className="text-[#B8915E]">·</span>
            <span>International Express</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
